import { createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { IS_DEVELOPMENT } from '~/modules/config';
import { getCompetencyFilters } from '~/modules/services/competencyFiltersService';
import { CompetencyFiltersResponse } from '~/modules/types/competencyFilters';
import { handleException } from '~/modules/utilities/error_utilities';

export interface CompetencyFiltersState extends State {
    data?: CompetencyFiltersResponse;
    status: 'idle' | 'loading' | 'loaded' | 'error';
}

const RELOAD_THROTTLE_MS = 5 * 60_000;
const RETRY_COOLDOWN_MS = 30_000;

let inFlight: Promise<void> | undefined;
// The server embeds its cached catalog in the base HTML; treat it as a fresh load.
const embedded = window.stores?.COMPETENCY_FILTERS;
let nextLoadAt = embedded ? Date.now() + RELOAD_THROTTLE_MS : 0;

export const competencyFiltersStore = createStore(
    (embedded ? { data: embedded, status: 'loaded' } : { status: 'idle' }) as CompetencyFiltersState,
    (set: SetState<CompetencyFiltersState>) => ({
        load(): Promise<void> {
            if (inFlight) {
                return inFlight;
            }
            if (Date.now() < nextLoadAt) {
                return Promise.resolve();
            }
            set(function loading(draft) {
                draft.status = 'loading';
            });
            inFlight = getCompetencyFilters()
                .then((data) => {
                    nextLoadAt = Date.now() + RELOAD_THROTTLE_MS;
                    set(function loaded(draft) {
                        draft.data = data;
                        draft.status = 'loaded';
                    });
                })
                .catch((error: unknown) => {
                    nextLoadAt = Date.now() + RETRY_COOLDOWN_MS;
                    // Logging guide Q3: anticipated degradation with static/last-successful fallback -> warn.
                    handleException(error, 'competencyFiltersStore.load', { level: 'warn' });
                    set(function failed(draft) {
                        draft.status = 'error';
                    });
                })
                .finally(() => {
                    inFlight = undefined;
                });
            return inFlight;
        },
    }),
    'competencyFiltersStore',
    IS_DEVELOPMENT,
);
